import {
  FIRST_PLACE_PRIZE_WORLD_SERIES_S5,
  INITIAL_PURSE_NEXT_WORLD_SERIES_S5,
  MIN_PRIZE_WORLD_SERIES_S5,
  PAID_PLAYERS_PCT_WORLD_SERIES_S5,
  PRIZE_SCALING_WORLD_SERIES_S5,
  SIDE_GAMES_WORLD_SERIES_S5,
} from "../../constants";

/**
 * World Series S5 Stage 1 and Stage 2 use the same calculation.
 *
 * M = floor(players * 30%). Rank 1 receives $30,000. For ranks 2..M,
 * weight = (M - rank + 1) * max(1, 15 * (1 - scaling)^(rank - 2)).
 * Ranks 3..M receive their weighted share of the remaining strokeplay pool,
 * with a $100 minimum. Second place absorbs the minimum-prize adjustments.
 * Second place is the unrounded remainder, as in the finance sheet, so
 * the table totals the $130,000 strokeplay pool to within a dollar or two.
 * Fewer than 2 paid places (up to 6 entrants) pays the winner only.
 *
 * Within the 1,000-player signup limit, second exceeds the
 * winner at 10..89 entrants; minimum-prize adjustments push second below
 * third at 950..1000.
 * Returns one entry per player, with zeroes for unpaid places.
 */
export const prizeCalculatorWorldSeriesS5 = (playersCount: number): number[] => {
  if (!Number.isSafeInteger(playersCount) || playersCount <= 0) return [];

  const prizes = new Array<number>(playersCount).fill(0);
  prizes[0] = FIRST_PLACE_PRIZE_WORLD_SERIES_S5;

  // Matches the finance sheet: floor(30%) paid places, winner only when that
  // is fewer than 2 (up to 6 entrants). 7+ entrants already pay a second place.
  const paidPlaces = Math.min(
    playersCount,
    Math.floor(playersCount * PAID_PLAYERS_PCT_WORLD_SERIES_S5)
  );
  if (paidPlaces < 2) return prizes;

  const pool =
    INITIAL_PURSE_NEXT_WORLD_SERIES_S5 -
    SIDE_GAMES_WORLD_SERIES_S5 -
    FIRST_PLACE_PRIZE_WORLD_SERIES_S5;
  const weightFor = (rank: number) =>
    (paidPlaces - rank + 1) *
    Math.max(1, 15 * Math.pow(1 - PRIZE_SCALING_WORLD_SERIES_S5, rank - 2));

  let totalWeight = 0;
  for (let rank = 2; rank <= paidPlaces; rank++) {
    totalWeight += weightFor(rank);
  }

  // The sheet keeps ranks unrounded and shows second as the remainder, so do
  // the same and round only for display.
  const table = new Array<number>(paidPlaces).fill(0);
  let awarded = 0;
  for (let rank = 3; rank <= paidPlaces; rank++) {
    const prize = Math.max(
      MIN_PRIZE_WORLD_SERIES_S5,
      (weightFor(rank) / totalWeight) * pool
    );
    table[rank - 1] = prize;
    awarded += prize;
  }
  table[1] = pool - awarded;

  for (let i = 1; i < paidPlaces; i++) prizes[i] = Math.round(table[i]);
  return prizes;
};
